/**
 * Client API pour le Analytics Service (TimescaleDB)
 * Couche d'abstraction pour les appels HTTP bruts
 */

import { HttpClient } from '@sphere/shared/data-access/http'
import type {
  UserSubjectMetrics,
  RecentAttempt,
  UserSummary,
  UserTeachingTimeline,
  UserLearningDifficulties,
  EstablishmentSummary,
  ClassMetrics,
  GradeMetrics,
  TopActivity, // Note: This will need reconciliation if mismatch persists
  DailyActivity,
  LearningActivityEvent,
} from '@sphere/analytics/domain';
import { TopActivitiesResponseDto } from '@sphere/analytics/domain';

// ============================================================================
// Analytics Client (nouveaux endpoints TimescaleDB)
// ============================================================================

export const analyticsClient = {
  // Ingestion d'événements
  ingest: {
    learningActivity: (event: LearningActivityEvent) =>
      HttpClient.post<{ event_id: string; status: string }>(
        '/analytics/ingest/learning-activity-completed',
        event
      ),
  },

  // Métriques utilisateur
  metrics: {
    getUserSubjectDaily: (
      userId: string,
      subjectId: string,
      params?: {
        date_from?: string
        date_to?: string
        limit?: number
      }
    ) =>
      HttpClient.get<UserSubjectMetrics>(
        `/analytics/metrics/user/${userId}/subject/${subjectId}/daily`,
        { params }
      ),

    getRecentAttempts: (
      userId: string,
      params?: {
        since_days?: number
        limit?: number
      }
    ) =>
      HttpClient.get<{
        user_id: string
        since_days: number
        attempts: RecentAttempt[]
      }>(`/analytics/metrics/user/${userId}/recent-attempts`, { params }),

    getUserSummary: (
      userId: string,
      params?: {
        since_days?: number
      }
    ) =>
      HttpClient.get<UserSummary>(`/analytics/metrics/user/${userId}/summary`, {
        params,
      }),

    getUserTeachingTimeline: (
      userId: string,
      teachingId: string,
      params?: {
        since_days?: number
        granularity?: 'day' | 'week'
        events_limit?: number
      }
    ) =>
      HttpClient.get<UserTeachingTimeline>(
        `/analytics/metrics/user/${userId}/teaching/${teachingId}/timeline`,
        { params }
      ),

    // Establishment metrics
    getEstablishmentSummary: (
      tenantId: string,
      params?: {
        since_days?: number
      }
    ) =>
      HttpClient.get<EstablishmentSummary>(
        `/analytics/metrics/establishment/${tenantId}/summary`,
        { params }
      ),

    getEstablishmentByClass: (
      tenantId: string,
      params?: {
        since_days?: number
      }
    ) =>
      HttpClient.get<{
        tenant_id: string
        since_days: number
        classes: ClassMetrics[]
      }>(`/analytics/metrics/establishment/${tenantId}/by-class`, { params }),

    getEstablishmentByGrade: (
      tenantId: string,
      params?: {
        since_days?: number
      }
    ) =>
      HttpClient.get<{
        tenant_id: string
        since_days: number
        grades: GradeMetrics[]
      }>(`/analytics/metrics/establishment/${tenantId}/by-grade`, { params }),

    getEstablishmentTopActivities: (
      tenantId: string,
      params?: {
        since_days?: number
        limit?: number
      }
    ) =>
      HttpClient.get<TopActivitiesResponseDto>(`/analytics/metrics/establishment/${tenantId}/top-activities`, {
        params,
      }),

    getEstablishmentDailyActivity: (
      tenantId: string,
      params?: {
        since_days?: number
      }
    ) =>
      HttpClient.get<{
        tenant_id: string
        since_days: number
        daily_metrics: DailyActivity[]
      }>(`/analytics/metrics/establishment/${tenantId}/daily-activity`, {
        params,
      }),
  },

  recommendations: {
    getUserDifficulties: (
      userId: string,
      params?: {
        since_days?: number
        teaching_id?: string
        subject_id?: string
        limit?: number
      }
    ) =>
      HttpClient.get<UserLearningDifficulties>(
        `/analytics/recommendations/user/${userId}/difficulties`,
        { params }
      ),
  },

  // Health check
  health: () =>
    HttpClient.get<{
      status: string
      service: string
      version: string
      database: string
    }>('/analytics/health'),
}
